import React, { useState, useEffect, useRef, FC, JSX, ChangeEvent, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import BackendStatus from '../../components/ui/BackendStatus';
import brandLogo from '../../assets/dhoondh2-logo.svg';
import authService from '../../services/auth.service';

interface ForgotPasswordProps {
  // No props defined in the original component, but keeping the interface for consistency
}

const ForgotPassword: FC<ForgotPasswordProps> = (): JSX.Element => {
  const [formData, setFormData] = useState<{ mobileNumber: string }>({
    mobileNumber: ''
  });
  const [errors, setErrors] = useState<{ mobileNumber?: string; submit?: string }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const heroRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
  
  // Animation on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { mobileNumber?: string; submit?: string } = {};

    if (!formData.mobileNumber) {
      newErrors.mobileNumber = 'mobileNumber is required';
    } if (!/^\d{10}$/.test(String(formData.mobileNumber).replace(/\D/g, ''))) {
      newErrors.mobileNumber = 'Enter a valid 10-digit mobile number';
    } 
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      // TODO use AuthContext
      const {data:userId} = await authService.forgotPassword(formData.mobileNumber);
      navigate('/password-setup', { state: { mobileNumber: formData.mobileNumber , userId  , requestType : "ResetPassword"} });
    } catch (error: any) {
      setErrors({ submit: (typeof error == 'object' && (error.data || error.message)) || 'Failed to send otp' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-teaGreen/5 via-white to-primary-lightGreen/10">
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-20 pb-16 md:pt-24 md:pb-20 overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-teaGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-lightGreen/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-teaGreen/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="mx-auto flex items-center justify-center mb-8">
              <img src={brandLogo} alt="Dhoondh" className="h-12 md:h-16 aspect-auto" />
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-primary-black mb-6 leading-tight">
              Forgot
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-teaGreen to-primary-lightGreen">
                {' '}Password?
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              No worries! Enter your mobileNumber and we'll send you a reset link
            </p>
          </div>
        </div>
      </section>

      {/* Forgot Password Form Section */}
      <section className="relative pb-20 md:pb-32">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <BackendStatus />
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary-teaGreen/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-primary-teaGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-primary-black mb-2">
                Reset your password
              </h2>
              <p className="text-gray-600">
                Enter your Mobile Number and we'll send you a otp to reset your password
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Mobile Number *
                </label>
                <input
                  type="number"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-teaGreen/20 ${
                    errors.mobileNumber 
                      ? 'border-red-300 focus:border-red-500' 
                      : 'border-gray-200 focus:border-primary-teaGreen'
                  }`}
                  placeholder="Enter your mobile number"
                  required
                />
                {errors.mobileNumber && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.mobileNumber}
                  </p>
                )}
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-700">{errors.submit}</p>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                className="w-full py-4 text-lg font-semibold"
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Sending Otp...' : 'Send OTP'}
              </Button>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Remember your password?{' '}
                  <Link to="/login" className="font-semibold text-primary-teaGreen hover:text-primary-lightGreen transition-colors">
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForgotPassword;
